// Cardio Entry Page - Quick entry for cardio workouts

import { query, saveDatabase, getDatabase } from '../db.js';
import { navigateTo, showToast } from '../router.js';

let activeTab = 'jogging';

export async function renderCardioEntryPage() {
  const app = document.getElementById('app');

  // Get locations for autocomplete
  const locations = query(`
    SELECT DISTINCT studio
    FROM PerformedWorkout
    WHERE studio IS NOT NULL AND studio != ''
    ORDER BY studio
  `).map(row => row.studio);

  const render = () => {
    app.innerHTML = `
      <div class="page safe-top">
        <div class="page-header">
          <h1 class="page-title">Log Cardio Workout</h1>
          <p class="text-sm text-muted">Quick entry for cardio sessions</p>
        </div>

        <div style="padding: 0 var(--spacing-md);">
          <!-- Tab Selector -->
          <div style="display: flex; gap: var(--spacing-sm); margin-bottom: var(--spacing-lg); background: var(--color-card); border-radius: var(--radius-lg); padding: var(--spacing-xs); border: 1px solid var(--color-border);">
            <button class="tab-button ${activeTab === 'jogging' ? 'active' : ''}" id="tab-jogging" style="flex: 1; padding: var(--spacing-sm); border: none; background: ${activeTab === 'jogging' ? 'linear-gradient(135deg, #FF9500 0%, #FFB647 100%)' : 'transparent'}; color: ${activeTab === 'jogging' ? 'white' : 'var(--color-text)'}; border-radius: var(--radius-md); cursor: pointer; font-weight: 500;">
              Jogging
            </button>
            <button class="tab-button ${activeTab === 'intervals' ? 'active' : ''}" id="tab-intervals" style="flex: 1; padding: var(--spacing-sm); border: none; background: ${activeTab === 'intervals' ? 'linear-gradient(135deg, #FF9500 0%, #FFB647 100%)' : 'transparent'}; color: ${activeTab === 'intervals' ? 'white' : 'var(--color-text)'}; border-radius: var(--radius-md); cursor: pointer; font-weight: 500;">
              Sprint Intervals
            </button>
          </div>

          <!-- Common Fields -->
          <div style="margin-bottom: var(--spacing-lg);">
            <label class="form-label">Date</label>
            <input type="date" id="cardio-date" class="form-input" value="${new Date().toISOString().split('T')[0]}" />
          </div>

          <div style="margin-bottom: var(--spacing-lg);">
            <label class="form-label">Time</label>
            <input type="time" id="cardio-time" class="form-input" value="${new Date().toTimeString().slice(0, 5)}" />
          </div>

          <div style="margin-bottom: var(--spacing-lg);">
            <label class="form-label">Location (optional)</label>
            <input type="text" id="cardio-location" class="form-input" list="locations-list" placeholder="Park, gym, track..." />
            <datalist id="locations-list">
              ${locations.map(loc => `<option value="${loc}">`).join('')}
            </datalist>
          </div>

          <!-- Jogging Form -->
          ${activeTab === 'jogging' ? renderJoggingForm() : ''}

          <!-- Sprint Intervals Form -->
          ${activeTab === 'intervals' ? renderSprintIntervalsForm() : ''}

          <!-- Notes -->
          <div style="margin-bottom: var(--spacing-lg);">
            <label class="form-label">Notes (optional)</label>
            <textarea id="cardio-notes" class="form-input" rows="3" maxlength="500" placeholder="How did it feel? Weather conditions?"></textarea>
            <p class="text-xs text-muted" style="margin-top: var(--spacing-xs);">
              <span id="notes-count">0</span>/500
            </p>
          </div>

          <!-- Save Button -->
          <button class="btn-primary" id="save-cardio-btn" style="width: 100%; margin-bottom: var(--spacing-xl);">
            Save Cardio Workout
          </button>
        </div>
      </div>
    `;

    attachEventListeners();
  };

  render();
}

function renderJoggingForm() {
  return `
    <div id="jogging-form">
      <div style="margin-bottom: var(--spacing-lg);">
        <label class="form-label">Distance (km)</label>
        <input type="number" id="jog-distance" class="form-input" step="0.1" min="0" placeholder="7.6" />
      </div>

      <div style="margin-bottom: var(--spacing-lg);">
        <label class="form-label">Elapsed Time</label>
        <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: var(--spacing-sm);">
          <div>
            <label class="text-xs text-muted" style="display: block; margin-bottom: var(--spacing-xs);">Hours</label>
            <input type="number" id="jog-hours" class="form-input" min="0" max="23" placeholder="0" style="text-align: center;" />
          </div>
          <div>
            <label class="text-xs text-muted" style="display: block; margin-bottom: var(--spacing-xs);">Minutes</label>
            <input type="number" id="jog-minutes" class="form-input" min="0" max="59" placeholder="45" style="text-align: center;" />
          </div>
          <div>
            <label class="text-xs text-muted" style="display: block; margin-bottom: var(--spacing-xs);">Seconds</label>
            <input type="number" id="jog-seconds" class="form-input" min="0" max="59" placeholder="0" style="text-align: center;" />
          </div>
        </div>
      </div>

      <!-- Calculated Metrics -->
      <div style="background: var(--color-card); border: 1px solid var(--color-border); border-radius: var(--radius-lg); padding: var(--spacing-md); margin-bottom: var(--spacing-lg);">
        <p class="text-xs text-muted" style="margin-bottom: var(--spacing-sm);">Auto-calculated</p>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-md);">
          <div>
            <p class="text-xs text-muted">Pace</p>
            <p class="stat-value" id="jog-pace" style="font-size: 1.5rem; font-weight: 600; margin: var(--spacing-xs) 0;">--</p>
            <p class="text-xs text-muted">min/km</p>
          </div>
          <div>
            <p class="text-xs text-muted">Speed</p>
            <p class="stat-value" id="jog-speed" style="font-size: 1.5rem; font-weight: 600; margin: var(--spacing-xs) 0;">--</p>
            <p class="text-xs text-muted">km/h</p>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderSprintIntervalsForm() {
  return `
    <div id="intervals-form">
      <div style="margin-bottom: var(--spacing-lg);">
        <label class="form-label">Sprint Speed (km/h)</label>
        <input type="number" id="sprint-speed" class="form-input" step="0.1" min="0" placeholder="18" />
      </div>

      <div style="margin-bottom: var(--spacing-lg);">
        <label class="form-label">Sprint Duration (seconds)</label>
        <input type="number" id="sprint-duration" class="form-input" min="1" placeholder="30" />
      </div>

      <div style="margin-bottom: var(--spacing-lg);">
        <label class="form-label">Rest Speed (km/h)</label>
        <input type="number" id="rest-speed" class="form-input" step="0.1" min="0" placeholder="9" />
      </div>

      <div style="margin-bottom: var(--spacing-lg);">
        <label class="form-label">Rest Duration (seconds)</label>
        <input type="number" id="rest-duration" class="form-input" min="1" placeholder="90" />
      </div>

      <div style="margin-bottom: var(--spacing-lg);">
        <label class="form-label">Number of Intervals</label>
        <input type="number" id="repetitions" class="form-input" min="1" placeholder="6" />
      </div>

      <!-- Calculated Metrics -->
      <div style="background: var(--color-card); border: 1px solid var(--color-border); border-radius: var(--radius-lg); padding: var(--spacing-md); margin-bottom: var(--spacing-lg);">
        <p class="text-xs text-muted" style="margin-bottom: var(--spacing-sm);">Auto-calculated</p>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-md);">
          <div>
            <p class="text-xs text-muted">Total Distance</p>
            <p class="stat-value" id="intervals-distance" style="font-size: 1.5rem; font-weight: 600; margin: var(--spacing-xs) 0;">--</p>
            <p class="text-xs text-muted">km</p>
          </div>
          <div>
            <p class="text-xs text-muted">Total Duration</p>
            <p class="stat-value" id="intervals-duration" style="font-size: 1.5rem; font-weight: 600; margin: var(--spacing-xs) 0;">--</p>
            <p class="text-xs text-muted">min</p>
          </div>
        </div>
      </div>
    </div>
  `;
}

function attachEventListeners() {
  // Tab switching
  document.getElementById('tab-jogging')?.addEventListener('click', () => {
    activeTab = 'jogging';
    renderCardioEntryPage();
  });

  document.getElementById('tab-intervals')?.addEventListener('click', () => {
    activeTab = 'intervals';
    renderCardioEntryPage();
  });

  // Notes character count
  document.getElementById('cardio-notes')?.addEventListener('input', (e) => {
    document.getElementById('notes-count').textContent = e.target.value.length;
  });

  // Jogging calculations
  const jogDistanceInput = document.getElementById('jog-distance');
  const jogHoursInput = document.getElementById('jog-hours');
  const jogMinutesInput = document.getElementById('jog-minutes');
  const jogSecondsInput = document.getElementById('jog-seconds');
  if (jogDistanceInput && jogHoursInput && jogMinutesInput && jogSecondsInput) {
    jogDistanceInput.addEventListener('input', updateJoggingCalculations);
    jogHoursInput.addEventListener('input', updateJoggingCalculations);
    jogMinutesInput.addEventListener('input', updateJoggingCalculations);
    jogSecondsInput.addEventListener('input', updateJoggingCalculations);
  }

  // Sprint intervals calculations
  const sprintSpeedInput = document.getElementById('sprint-speed');
  const sprintDurationInput = document.getElementById('sprint-duration');
  const restSpeedInput = document.getElementById('rest-speed');
  const restDurationInput = document.getElementById('rest-duration');
  const repsInput = document.getElementById('repetitions');
  if (sprintSpeedInput) {
    sprintSpeedInput.addEventListener('input', updateIntervalsCalculations);
    sprintDurationInput.addEventListener('input', updateIntervalsCalculations);
    restSpeedInput.addEventListener('input', updateIntervalsCalculations);
    restDurationInput.addEventListener('input', updateIntervalsCalculations);
    repsInput.addEventListener('input', updateIntervalsCalculations);
  }

  // Save button
  document.getElementById('save-cardio-btn')?.addEventListener('click', saveCardioWorkout);
}

function updateJoggingCalculations() {
  const distance = parseFloat(document.getElementById('jog-distance').value) || 0;
  const hours = parseInt(document.getElementById('jog-hours').value) || 0;
  const minutes = parseInt(document.getElementById('jog-minutes').value) || 0;
  const seconds = parseInt(document.getElementById('jog-seconds').value) || 0;

  const totalSeconds = hours * 3600 + minutes * 60 + seconds;

  if (distance > 0 && totalSeconds > 0) {
    const { pace, speed } = calculateJoggingMetrics(distance, totalSeconds);
    document.getElementById('jog-pace').textContent = pace;
    document.getElementById('jog-speed').textContent = speed;
  } else {
    document.getElementById('jog-pace').textContent = '--';
    document.getElementById('jog-speed').textContent = '--';
  }
}

function updateIntervalsCalculations() {
  const sprintSpeed = parseFloat(document.getElementById('sprint-speed').value) || 0;
  const sprintDuration = parseFloat(document.getElementById('sprint-duration').value) || 0;
  const restSpeed = parseFloat(document.getElementById('rest-speed').value) || 0;
  const restDuration = parseFloat(document.getElementById('rest-duration').value) || 0;
  const reps = parseInt(document.getElementById('repetitions').value) || 0;

  if (sprintSpeed > 0 && sprintDuration > 0 && restSpeed >= 0 && restDuration > 0 && reps > 0) {
    const { totalDistance, totalDuration } = calculateIntervalMetrics(
      sprintSpeed, sprintDuration, restSpeed, restDuration, reps
    );
    document.getElementById('intervals-distance').textContent = totalDistance;
    document.getElementById('intervals-duration').textContent = totalDuration;
  } else {
    document.getElementById('intervals-distance').textContent = '--';
    document.getElementById('intervals-duration').textContent = '--';
  }
}

function calculateJoggingMetrics(distance_km, duration_seconds) {
  const duration_minutes = duration_seconds / 60;
  const pace_minutes = duration_minutes / distance_km;
  const speed = distance_km / (duration_minutes / 60);

  // Format pace as MM:SS
  const paceMinutes = Math.floor(pace_minutes);
  const paceSeconds = Math.round((pace_minutes - paceMinutes) * 60);
  const paceFormatted = `${paceMinutes}:${paceSeconds.toString().padStart(2, '0')}`;

  return {
    pace: paceFormatted,
    paceDecimal: pace_minutes, // Keep for database storage
    speed: speed.toFixed(2)
  };
}

function calculateIntervalMetrics(sprintSpeed, sprintDuration, restSpeed, restDuration, reps) {
  const sprintDistancePerRep = sprintSpeed * (sprintDuration / 3600);
  const restDistancePerRep = restSpeed * (restDuration / 3600);
  const totalDistance = (sprintDistancePerRep + restDistancePerRep) * reps;
  const totalDuration = (sprintDuration + restDuration) * reps / 60;
  return {
    totalDistance: totalDistance.toFixed(2),
    totalDuration: Math.round(totalDuration)
  };
}

async function saveCardioWorkout() {
  const date = document.getElementById('cardio-date').value;
  const time = document.getElementById('cardio-time').value;
  const location = document.getElementById('cardio-location').value.trim();
  const notes = document.getElementById('cardio-notes').value.trim();

  if (activeTab === 'jogging') {
    await saveJoggingEntry(date, time, location, notes);
  } else {
    await saveSprintIntervalsEntry(date, time, location, notes);
  }
}

async function saveJoggingEntry(date, time, location, notes) {
  const distance = parseFloat(document.getElementById('jog-distance').value);
  const hours = parseInt(document.getElementById('jog-hours').value) || 0;
  const minutes = parseInt(document.getElementById('jog-minutes').value) || 0;
  const seconds = parseInt(document.getElementById('jog-seconds').value) || 0;

  const totalSeconds = hours * 3600 + minutes * 60 + seconds;
  const duration_minutes = totalSeconds / 60;

  if (!distance || distance <= 0) {
    showToast('Please enter a valid distance', 'error');
    return;
  }

  if (totalSeconds <= 0) {
    showToast('Please enter a valid duration', 'error');
    return;
  }

  const { paceDecimal, speed } = calculateJoggingMetrics(distance, totalSeconds);
  const db = getDatabase();

  // 1. Get or create jogging exercise
  let exercise = query(
    `SELECT id FROM Exercises WHERE exercise_type = 'cardio_jogging' LIMIT 1`
  )[0];

  if (!exercise) {
    db.run(
      `INSERT INTO Exercises (name, musclegroup, equipment, color, exercise_type) VALUES (?, ?, ?, ?, ?)`,
      ['Jogging', 'cardio', 'Outdoor', '#FF9500', 'cardio_jogging']
    );
    exercise = query(`SELECT id FROM Exercises WHERE name = 'Jogging'`)[0];
  }

  // 2. Create PerformedWorkout
  db.run(
    `INSERT INTO PerformedWorkout (template_id, workout_type, date, start_time, end_time, studio, notes)
     VALUES (?, ?, ?, ?, ?, ?, ?)`,
    [null, 'cardio', date, time, time, location || null, notes || null]
  );

  const workoutId = query(`SELECT last_insert_rowid() as id`)[0].id;

  // 3. Create PerformedSets
  db.run(
    `INSERT INTO PerformedSets (workout_instance_id, exercise_id, set_number)
     VALUES (?, ?, ?)`,
    [workoutId, exercise.id, 1]
  );

  const setId = query(`SELECT last_insert_rowid() as id`)[0].id;

  // 4. Create CardioData
  db.run(
    `INSERT INTO CardioData (performed_set_id, distance_km, duration_minutes, pace_min_per_km, speed_km_per_h)
     VALUES (?, ?, ?, ?, ?)`,
    [setId, distance, duration_minutes, paceDecimal, parseFloat(speed)]
  );

  await saveDatabase();
  showToast('Jogging session saved!', 'success');
  navigateTo('/history');
}

async function saveSprintIntervalsEntry(date, time, location, notes) {
  const sprintSpeed = parseFloat(document.getElementById('sprint-speed').value);
  const sprintDuration = parseInt(document.getElementById('sprint-duration').value);
  const restSpeed = parseFloat(document.getElementById('rest-speed').value);
  const restDuration = parseInt(document.getElementById('rest-duration').value);
  const reps = parseInt(document.getElementById('repetitions').value);

  if (!sprintSpeed || sprintSpeed <= 0) {
    showToast('Please enter a valid sprint speed', 'error');
    return;
  }

  if (!sprintDuration || sprintDuration <= 0) {
    showToast('Please enter a valid sprint duration', 'error');
    return;
  }

  if (restSpeed < 0) {
    showToast('Please enter a valid rest speed', 'error');
    return;
  }

  if (!restDuration || restDuration <= 0) {
    showToast('Please enter a valid rest duration', 'error');
    return;
  }

  if (!reps || reps < 1) {
    showToast('Please enter a valid number of intervals', 'error');
    return;
  }

  const { totalDistance, totalDuration } = calculateIntervalMetrics(
    sprintSpeed, sprintDuration, restSpeed, restDuration, reps
  );

  const db = getDatabase();

  // 1. Get or create sprint intervals exercise
  let exercise = query(
    `SELECT id FROM Exercises WHERE exercise_type = 'cardio_intervals' LIMIT 1`
  )[0];

  if (!exercise) {
    db.run(
      `INSERT INTO Exercises (name, musclegroup, equipment, color, exercise_type) VALUES (?, ?, ?, ?, ?)`,
      ['Sprint Intervals', 'cardio', 'Treadmill', '#FF9500', 'cardio_intervals']
    );
    exercise = query(`SELECT id FROM Exercises WHERE name = 'Sprint Intervals'`)[0];
  }

  // 2. Create PerformedWorkout
  db.run(
    `INSERT INTO PerformedWorkout (template_id, workout_type, date, start_time, end_time, studio, notes)
     VALUES (?, ?, ?, ?, ?, ?, ?)`,
    [null, 'cardio', date, time, time, location || null, notes || null]
  );

  const workoutId = query(`SELECT last_insert_rowid() as id`)[0].id;

  // 3. Create PerformedSets
  db.run(
    `INSERT INTO PerformedSets (workout_instance_id, exercise_id, set_number)
     VALUES (?, ?, ?)`,
    [workoutId, exercise.id, 1]
  );

  const setId = query(`SELECT last_insert_rowid() as id`)[0].id;

  // 4. Create CardioData
  db.run(
    `INSERT INTO CardioData (
      performed_set_id,
      sprint_speed_kmh,
      sprint_duration_sec,
      rest_speed_kmh,
      rest_duration_sec,
      repetitions,
      total_distance_km,
      total_duration_minutes
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
    [setId, sprintSpeed, sprintDuration, restSpeed, restDuration, reps, parseFloat(totalDistance), totalDuration]
  );

  await saveDatabase();
  showToast('Sprint intervals session saved!', 'success');
  navigateTo('/history');
}
